// ErrorState — a failed fetch, shown honestly, with a way out.
//
// The old admin mostly rendered a failed query as an empty table, which is
// actively misleading: "no invoices" and "we could not load invoices" are
// opposite facts and looked identical. This component always distinguishes
// them, and always offers retry when the caller can retry.
//
// It also maps the API's error CODES to something a staff user can act on,
// rather than surfacing a raw message. Unknown codes fall through to the
// server's own message, which is written for humans.

import { cn } from '@fajrarabi/ui';

import { Icon } from './Icon';

import { ApiError } from '~/lib/api';

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
  size?: 'compact' | 'page';
}

/** Human-facing message for any thrown value. Never returns an empty string. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'forbidden':
        return "Your account doesn't have permission to view this.";
      case 'unauthorized':
        return 'Your session expired. Reload the page to sign in again.';
      case 'not_found':
        return 'That record no longer exists.';
      case 'rate_limited':
        return 'Too many requests. Wait a moment and try again.';
      default:
        return error.message || `Request failed (${error.status}).`;
    }
  }
  if (error instanceof Error) {
    // A fetch that never reached the server surfaces as a TypeError with a
    // browser-specific message ("Failed to fetch", "NetworkError…"). Staff
    // need "you are offline", not that string.
    if (error.name === 'TypeError') {
      return 'Could not reach the server. Check your connection and try again.';
    }
    return error.message || 'Something went wrong.';
  }
  return 'Something went wrong.';
}

/** True when retrying could plausibly succeed — 4xx client errors cannot. */
function isRetryable(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500 || error.status === 429;
  return true;
}

export function ErrorState({ error, onRetry, className, size = 'compact' }: ErrorStateProps) {
  const message = errorMessage(error);
  const showRetry = Boolean(onRetry) && isRetryable(error);
  const status = error instanceof ApiError ? error.status : null;

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'page' ? 'min-h-[60vh] px-6 py-16' : 'px-6 py-14',
        className,
      )}
    >
      <div className="grid h-12 w-12 place-items-center rounded-full bg-error-50 text-error-500">
        <Icon name="alert" className="h-6 w-6" />
      </div>
      <p className="mt-4 text-sm font-semibold text-admin-800">Couldn&rsquo;t load this</p>
      <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-admin-500">{message}</p>
      {showRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex items-center gap-1.5 rounded-lg border border-admin-200 bg-white px-3.5 py-2 text-sm font-medium text-admin-700 transition-colors hover:bg-admin-50"
        >
          <Icon name="refresh" className="h-4 w-4" />
          Try again
        </button>
      )}
      {status != null && (
        <p className="mt-3 text-[11px] uppercase tracking-wider text-admin-400">Error {status}</p>
      )}
    </div>
  );
}
